import { FC, useEffect, useMemo, useState } from 'react';

import { XIcon } from '@heroicons/react/outline';
import {
  getCouponRefundPendingRows,
  getCouponSendPendingRows,
} from '@shared/api/couponSettlement/couponSettlement.controller';
import {
  CouponPendingRowBase,
  GiftCouponSettlementSummaryItem,
} from '@shared/api/couponSettlement/couponSettlement.interface';
import { Loading } from '@shared/components/Loading';
import { LowerRole } from '@shared/types';
import { useAsyncFn } from 'react-use';

import {
  couponCodeLabel,
  matchPendingRow,
  MAX_ENUM_IDS,
  monthYmdRange,
  ymdRangeToIso,
} from '../../utils/coupon-settlement.utils';
import { formatDate } from '../../utils/delivery.utils';
import { CouponSearchInput } from './CouponSearchInput';
import { DateFilter } from './DateFilter';

// ─────────────────────────────────────────────
// CouponAddPendingModal (Admin 전용)
//
// Draft 정산서에 미정산 쿠폰을 골라 추가하는 피커. 해당 상조의 미정산
// 발송/환불 raw를 **기간(활동일)으로 조회**해 — 기본값은 정산서의 연월,
// 소급 추가는 기간을 바꾸거나 전체기간으로 — 검색으로 좁혀 체크한다.
// 선택은 쿠폰 단위 — 발송 후 환불된 쿠폰(양쪽 목록에 존재)은 한쪽만 넣으면
// 상쇄가 깨지므로 어느 탭에서 체크해도 함께 추가된다. 기간 재조회 시 선택은
// 초기화한다(로드 밖 선택은 어차피 제출 목록에서 빠져 조용히 유실되므로).
// 구매수량 초과(overQuota) 발송도 개별 체크로 강제 포함 가능(스펙 §5-2b —
// allowOverQuotaCouponIds로 쿼터 가드 면제, 관리자 명시 선택만). 단 전체선택에는
// 안 딸려 온다 — 초과 지급은 건별로 의도한 선택이어야 해서다. 백엔드 미반영
// 구서버에선 skipped(overQuota)로 반려될 뿐 오지급은 없다.
// 실제 PATCH·재조회는 호출부(CouponHistorySection) 몫.
// ─────────────────────────────────────────────

export interface CouponAddPendingModalProps {
  /** 추가 대상 정산서 (bugoBrandId·연월 표시용) */
  settlement: GiftCouponSettlementSummaryItem;
  /** 추가 PATCH 진행 중 여부 */
  busy: boolean;
  onClose: () => void;
  /**
   * 선택 확정 — 발송/환불로 나눠 전달 (쿠폰 단위 선택을 목록 소속으로 분배).
   * overQuotaSendIds는 sendIds의 부분집합 — 강제 포함할 초과 발송분
   * (PATCH allowOverQuotaCouponIds로 전달).
   */
  onSubmit: (sendIds: string[], refundIds: string[], overQuotaSendIds: string[]) => void;
}

/** 검색 없이 펼치기엔 미정산이 수천 건일 수 있어 탭당 표시 상한을 둔다 (선택은 유지됨) */
const MAX_VISIBLE = 50;

/** 발송·환불 행을 한 테이블로 그리기 위한 공통 표시형 */
type PickerRow = CouponPendingRowBase & {
  /** 발송일 또는 환불일 */
  activityAt?: string;
  amount: number;
  overQuota: boolean;
  /** 도매가(발송 실비) — 미동의 초과 행의 −도매가(낙전 회수) 표기용 (발송 행만) */
  priceWhole?: number;
  /** 재발행 발송 — 지급 대상, 뱃지 표기(Admin 전용 화면) */
  isAdditional?: boolean;
  /** 환불 탭의 연장 차감 행 — 같은 쿠폰의 환불 행과 key가 겹치지 않게 구분 */
  isRenewal?: boolean;
};

export const CouponAddPendingModal: FC<CouponAddPendingModalProps> = ({
  settlement,
  busy,
  onClose,
  onSubmit,
}) => {
  const [{ loading: sendLoading, value: sendRows }, fetchSendRows] = useAsyncFn(
    (from: string, to: string) =>
      getCouponSendPendingRows(
        { bugoBrand: settlement.bugoBrandId, ...ymdRangeToIso(from, to) },
        LowerRole.Admin,
      ),
    [settlement.bugoBrandId],
  );
  const [{ loading: refundLoading, value: refundRows }, fetchRefundRows] = useAsyncFn(
    (from: string, to: string) =>
      getCouponRefundPendingRows(
        { bugoBrand: settlement.bugoBrandId, ...ymdRangeToIso(from, to) },
        LowerRole.Admin,
      ),
    [settlement.bugoBrandId],
  );

  const [tab, setTab] = useState<'send' | 'refund'>('send');
  const [query, setQuery] = useState('');
  // 선택된 쿠폰 id (쿠폰 단위 — 양쪽 목록에 있는 쿠폰은 함께 추가된다)
  const [sel, setSel] = useState<Set<string>>(new Set());
  // 초과(overQuota) 발송 강제 포함 "동의" — 발송 탭의 초과 행을 직접 체크했을 때만.
  // 환불 탭 체크(쿠폰 단위 sel)만으로 초과 발송까지 지급되는 누수를 막는 분리 Set.
  const [overQuotaConsent, setOverQuotaConsent] = useState<Set<string>>(new Set());

  // 기간(활동일 — 발송 deliveredAt / 환불 refundedAt) 기본값 = 정산서 연월
  const defaultRange = monthYmdRange(settlement.year, settlement.month);
  const [dateFrom, setDateFrom] = useState(defaultRange.from);
  const [dateTo, setDateTo] = useState(defaultRange.to);

  /** 기간 조회 — 선택·검색 초기화 후 재로드 (로드 밖 선택의 조용한 유실 방지) */
  const reload = (from: string, to: string) => {
    setSel(new Set());
    setOverQuotaConsent(new Set());
    setQuery('');
    fetchSendRows(from, to);
    fetchRefundRows(from, to);
  };
  useEffect(() => {
    reload(defaultRange.from, defaultRange.to);
    // 최초 마운트 1회 로드 (기본 기간 = 정산서 연월)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 기간 일괄 변경(월별 선택·이번달·지난달·전체기간) — 값 반영과 동시에 재조회 */
  const handleRange = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
    reload(from, to);
  };

  const sends = useMemo(() => sendRows ?? [], [sendRows]);
  const refunds = useMemo(() => refundRows ?? [], [refundRows]);

  const visibleSends = useMemo(
    () => sends.filter((r) => matchPendingRow(r, query)),
    [sends, query],
  );
  const visibleRefunds = useMemo(
    () => refunds.filter((r) => matchPendingRow(r, query)),
    [refunds, query],
  );
  // 현재 탭의 검색 결과 건수 (상한 안내·빈 상태용)
  const activeCount = tab === 'send' ? visibleSends.length : visibleRefunds.length;
  // 표시 행 — 상한으로 자른 뒤에만 표시형으로 복사 (수천 행 전체 복사 방지)
  const activeShown: PickerRow[] = useMemo(
    () =>
      tab === 'send'
        ? visibleSends.slice(0, MAX_VISIBLE).map((r) => ({
            ...r,
            activityAt: r.deliveredAt,
            overQuota: r.overQuota === true,
          }))
        : visibleRefunds.slice(0, MAX_VISIBLE).map((r) => ({
            ...r,
            activityAt: r.refundedAt,
            overQuota: false,
          })),
    [tab, visibleSends, visibleRefunds],
  );

  /**
   * 행 토글. 일반 행 = 쿠폰 단위 선택(sel). 초과(overQuota) 행 = 발송 탭에만 있고,
   * 체크가 곧 강제 포함 동의(sel+동의 함께), 해제는 둘 다 해제. 일반 행을 해제할 때도
   * 동의를 같이 지워, 환불 탭에서 풀었는데 동의만 남는 상태를 막는다.
   */
  const toggle = (row: PickerRow) => {
    const id = row.giftCouponId;
    if (row.overQuota) {
      const consenting = !overQuotaConsent.has(id);
      setOverQuotaConsent((prev) => {
        const next = new Set(prev);
        if (consenting) next.add(id);
        else next.delete(id);
        return next;
      });
      setSel((prev) => {
        const next = new Set(prev);
        if (consenting) next.add(id);
        else next.delete(id);
        return next;
      });
      return;
    }
    const removing = sel.has(id);
    setSel((prev) => {
      const next = new Set(prev);
      if (removing) next.delete(id);
      else next.add(id);
      return next;
    });
    if (removing)
      setOverQuotaConsent((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
  };

  // 헤더 전체선택 — 현재 탭의 검색 결과 전체(표시 상한 50행 밖 포함) 기준.
  // 초과(overQuota) 행은 전체선택에서 제외 — 강제 포함은 건별 의도 선택만 허용.
  // 발송/환불 행의 유니온 배열은 메서드 호출이 안 돼(TS2349) 공통 베이스로 명시한다.
  const selectableAll = useMemo<CouponPendingRowBase[]>(
    () =>
      tab === 'send' ? visibleSends.filter((r) => r.overQuota !== true) : visibleRefunds,
    [tab, visibleSends, visibleRefunds],
  );
  const allChecked =
    selectableAll.length > 0 && selectableAll.every((r) => sel.has(r.giftCouponId));
  const toggleAll = () =>
    setSel((prev) => {
      const next = new Set(prev);
      selectableAll.forEach((r) =>
        allChecked ? next.delete(r.giftCouponId) : next.add(r.giftCouponId),
      );
      return next;
    });

  // 선택 요약 — 표시용 원값 합 (확정 정산금은 서버가 전체 라인 기준으로 재계산).
  // 초과(overQuota) 발송은 동의된 것만 제출·합계 대상 — 미동의 초과는 발송에서 빠지고
  // (서버에 보내봤자 반려), 환불 라인만 추가된다.
  const selectedSends = useMemo(
    () =>
      sends.filter(
        (r) =>
          sel.has(r.giftCouponId) &&
          (r.overQuota !== true || overQuotaConsent.has(r.giftCouponId)),
      ),
    [sends, sel, overQuotaConsent],
  );
  const selectedOverQuotaSends = useMemo(
    () => selectedSends.filter((r) => r.overQuota === true),
    [selectedSends],
  );
  const selectedRefunds = useMemo(
    () => refunds.filter((r) => sel.has(r.giftCouponId)),
    [refunds, sel],
  );
  const selectedCount = useMemo(() => {
    const ids = new Set<string>();
    selectedSends.forEach((r) => ids.add(r.giftCouponId));
    selectedRefunds.forEach((r) => ids.add(r.giftCouponId));
    return ids.size;
  }, [selectedSends, selectedRefunds]);
  const selectedSendSum = selectedSends.reduce((s, r) => s + r.amount, 0);
  const selectedRefundSum = selectedRefunds.reduce((s, r) => s + r.amount, 0);
  // add*는 ID 열거 PATCH라 body 한도 안에서만 — 초과 시 기간을 좁혀 나눠 추가
  const overIdLimit = selectedSends.length + selectedRefunds.length > MAX_ENUM_IDS;

  const loading = sendLoading || refundLoading;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative my-4 flex max-h-[88vh] w-full max-w-[900px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              쿠폰 추가 — {settlement.year}년 {settlement.month}월{' '}
              {settlement.bugoBrandName}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              이 상조의 미정산 쿠폰을 기간으로 조회해 고릅니다. 발송 후 환불된 쿠폰은
              발송·환불이 함께 추가됩니다.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="닫기"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* 기간 필터 — 기본값은 정산서 연월, 소급 추가는 기간 변경/전체기간으로.
            월/단축 선택은 즉시 재조회(onRange), 직접 입력은 조회 버튼으로. */}
        <div className="px-5 pt-3">
          <DateFilter
            label="기간(발송/환불완료일)"
            dateFrom={dateFrom}
            dateTo={dateTo}
            onFrom={setDateFrom}
            onTo={setDateTo}
            onRange={handleRange}
          >
            <button
              onClick={() => reload(dateFrom, dateTo)}
              className="rounded-lg bg-gray-700 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-gray-800"
            >
              조회
            </button>
          </DateFilter>
        </div>

        {loading ? (
          <div className="center-box py-20">
            <Loading />
          </div>
        ) : (
          <>
            {/* 툴바 — 발송/환불 탭 + 검색 */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
              <div className="flex gap-2">
                <button
                  onClick={() => setTab('send')}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium ${
                    tab === 'send'
                      ? 'border-emerald-600 bg-emerald-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  발송 수익금 · {sends.length}건
                  {selectedSends.length > 0 && ` (선택 ${selectedSends.length})`}
                </button>
                <button
                  onClick={() => setTab('refund')}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium ${
                    tab === 'refund'
                      ? 'border-rose-600 bg-rose-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  환불 차감 · {refunds.length}건
                  {selectedRefunds.length > 0 && ` (선택 ${selectedRefunds.length})`}
                </button>
              </div>
              <CouponSearchInput value={query} onChange={setQuery} />
            </div>

            {/* 표시 상한 안내 */}
            {activeCount > MAX_VISIBLE && (
              <div className="border-b border-amber-100 bg-amber-50 px-5 py-2 text-xs text-amber-700">
                {activeCount}건 중 {MAX_VISIBLE}건만 표시 중 — 검색으로 좁혀주세요. (이미
                체크한 선택은 유지됩니다)
              </div>
            )}

            {/* 목록 */}
            <div className="flex-1 overflow-auto px-5 py-4">
              <div className="overflow-x-auto rounded-lg bg-white shadow">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="sticky top-0 z-10 bg-gray-50 text-xs text-gray-500">
                    <tr>
                      <th className="w-10 px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          onChange={toggleAll}
                          disabled={selectableAll.length === 0}
                          title="검색 결과 전체 선택/해제 (표시 상한 밖 행 포함)"
                          className="h-4 w-4 rounded border-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
                        />
                      </th>
                      <th className="px-3 py-2 text-left">쿠폰번호</th>
                      <th className="px-3 py-2 text-left">구매상품명</th>
                      <th className="px-3 py-2 text-left">
                        <p>구매자</p>
                        <p className="mt-0.5">고인명</p>
                      </th>
                      <th className="px-3 py-2 text-left">
                        <p>구매일</p>
                        <p className="mt-0.5">{tab === 'send' ? '발송일' : '환불일'}</p>
                      </th>
                      <th className="px-3 py-2 text-right">
                        {tab === 'send' ? '정산금(+)' : '차감액(−)'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {activeShown.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                          {activeCount === 0
                            ? '이 기간에 추가할 미정산 건이 없습니다 — 기간을 넓히거나 전체기간으로 조회해 보세요.'
                            : '검색 결과가 없습니다.'}
                        </td>
                      </tr>
                    ) : (
                      activeShown.map((r) => {
                        // 초과 행은 "동의"가 체크 — 환불 탭에서 딸려 온 sel만으로는 미체크 표시
                        const checked = r.overQuota
                          ? overQuotaConsent.has(r.giftCouponId)
                          : sel.has(r.giftCouponId);
                        return (
                          <tr
                            key={`${r.giftCouponId}-${r.isRenewal ? 'renewal' : 'base'}`}
                            onClick={() => toggle(r)}
                            title={
                              r.overQuota
                                ? '구매수량 초과 발송 — 구매금액 0원 건으로, 도매가(발송 실비)는 낙전 정산에서 상계 예정입니다. 체크하면 쿼터 예외로 강제 포함되어 지급 대상이 됩니다.'
                                : undefined
                            }
                            className={`cursor-pointer ${
                              checked
                                ? r.overQuota
                                  ? 'bg-amber-100/70'
                                  : tab === 'send'
                                  ? 'bg-emerald-50/60'
                                  : 'bg-rose-50/60'
                                : r.overQuota
                                ? 'bg-amber-50/60 hover:bg-amber-50'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-3 py-2.5 text-center">
                              <input
                                type="checkbox"
                                checked={checked}
                                readOnly
                                className="h-4 w-4 rounded border-gray-300"
                              />
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-700">
                              {couponCodeLabel(r.fullCode)}
                              {r.overQuota && (
                                <span className="ml-1.5 inline-block whitespace-nowrap rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-700">
                                  구매수량 초과
                                </span>
                              )}
                              {r.isAdditional && (
                                <span className="ml-1.5 inline-block whitespace-nowrap rounded bg-sky-100 px-1 py-0.5 text-[10px] font-medium text-sky-700">
                                  재발행
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-xs font-medium text-gray-700">
                              {r.purchaseItemName}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-gray-600">
                              <p className="font-bold text-gray-700">
                                {r.buyerName || '-'}
                              </p>
                              <p className="mt-0.5 text-gray-400">
                                {r.deceasedName || '-'}
                              </p>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500">
                              <p>{formatDate(r.purchasedAt)}</p>
                              <p className="mt-0.5 font-bold text-gray-700">
                                {r.isRenewal && '연장 '}
                                {formatDate(r.activityAt)}
                              </p>
                            </td>
                            <td
                              className={`whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums ${
                                r.overQuota
                                  ? checked
                                    ? 'text-amber-700' // 강제 포함 선택됨 — 지급 대상
                                    : 'text-rose-700'
                                  : tab === 'send'
                                  ? 'text-emerald-700'
                                  : 'text-rose-700'
                              }`}
                            >
                              {/* 미동의 초과 = 구매금액 0원 건 → 정산금 −도매가(낙전 회수).
                                  체크(강제 포함)하면 정식 지급 라인이 되어 +정산금으로 전환 */}
                              {r.overQuota && !checked
                                ? r.priceWhole != null
                                  ? `−${r.priceWhole.toLocaleString('ko-KR')}`
                                  : '-'
                                : `${tab === 'send' ? '+' : '−'}${r.amount.toLocaleString(
                                    'ko-KR',
                                  )}`}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 하단 바 — 선택 요약 + 추가 확정 */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-3.5">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                <span>
                  발송{' '}
                  <span className="font-semibold text-emerald-700">
                    +{selectedSendSum.toLocaleString('ko-KR')}
                  </span>{' '}
                  ({selectedSends.length}건)
                </span>
                <span className="text-gray-300">·</span>
                <span>
                  환불{' '}
                  <span className="font-semibold text-rose-700">
                    −{selectedRefundSum.toLocaleString('ko-KR')}
                  </span>{' '}
                  ({selectedRefunds.length}건)
                </span>
                {selectedOverQuotaSends.length > 0 && (
                  <span className="text-xs font-medium text-amber-700">
                    ⚠ 구매수량 초과 {selectedOverQuotaSends.length}건 강제 포함 — 지급
                    대상이 되며 낙전 차감 대상에서 빠집니다.
                  </span>
                )}
                {overIdLimit ? (
                  <span className="text-xs font-medium text-rose-600">
                    한 번에 최대 {MAX_ENUM_IDS.toLocaleString('ko-KR')}건까지 추가할 수
                    있습니다 — 기간을 좁혀 나눠 추가해 주세요.
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">
                    확정 정산금은 추가 후 서버가 전체 라인으로 재계산합니다.
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  disabled={busy}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  onClick={() =>
                    onSubmit(
                      selectedSends.map((r) => r.giftCouponId),
                      selectedRefunds.map((r) => r.giftCouponId),
                      selectedOverQuotaSends.map((r) => r.giftCouponId),
                    )
                  }
                  disabled={busy || selectedCount === 0 || overIdLimit}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy ? '추가 중...' : `정산서에 추가 (${selectedCount}건)`}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
